import { MDXContent } from "@content-collections/mdx/react";
import { createFileRoute } from "@tanstack/react-router";

import { allPosts } from "content-collections";
import { CodeBlock } from "@/components/mdx/CodeBlock";

export const Route = createFileRoute("/_layout/blog/$slug")({
	component: RouteComponent,
	loader: async ({ params }) => {
		const post = allPosts.find((post) => post._meta.path === params.slug);
		if (!post) throw new Error("Post not found");
		return { post };
	},
	head: ({ loaderData }) => {
		const { post } = loaderData;
		const breadcrumbSchema = {
			"@context": "https://schema.org",
			"@type": "BreadcrumbList",
			"itemListElement": [
				{
					"@type": "ListItem",
					"position": 1,
					"name": "Home",
					"item": "https://macrowave.co",
				},
				{
					"@type": "ListItem",
					"position": 2,
					"name": "Blog",
					"item": "https://macrowave.co/blog",
				},
				{
					"@type": "ListItem",
					"position": 3,
					"name": post.title,
					"item": `https://macrowave.co/blog/${post._meta.path}`,
				},
			],
		};

		const articleSchema = {
			"@context": "https://schema.org",
			"@type": "BlogPosting",
			"headline": post.title,
			"description": post.summary,
			"image": post.image ? `https://macrowave.co${post.image}` : undefined,
			"url": `https://macrowave.co/blog/${post._meta.path}`,
			"datePublished": post.publishedAt,
			"dateModified": post.lastModified || post.publishedAt,
			"wordCount": post.wordCount,
			"timeRequired": `PT${post.readingTime}M`,
			"author": post.author
				? {
						"@type": "Person",
						"name": post.author.displayName,
						"url": post.author.website,
						"image": post.author.avatar ? `https://macrowave.co${post.author.avatar}` : undefined,
						"sameAs": [
							post.author.website,
							post.author.twitter,
							post.author.github,
						].filter(Boolean),
				  }
				: {
						"@type": "Organization",
						"name": "Macrowave",
						"url": "https://macrowave.co",
				  },
			"publisher": {
				"@type": "Organization",
				"name": "Macrowave",
				"url": "https://macrowave.co",
			},
		};

		return {
			links: [
				{
					rel: "canonical",
					href: `https://macrowave.co/blog/${post._meta.path}`,
				},
			],
			meta: [
				{
					name: "description",
					content: post.summary,
				},
				{
					property: "og:title",
					content: post.title,
				},
				{
					property: "og:description",
					content: post.summary,
				},
				{
					property: "og:type",
					content: "article",
				},
				{
					property: "og:url",
					content: `https://macrowave.co/blog/${post._meta.path}`,
				},
				...(post.image
					? [
							{
								property: "og:image",
								content: `https://macrowave.co${post.image}`,
							},
					  ]
					: []),
				{
					name: "twitter:card",
					content: "summary_large_image",
				},
				{
					name: "twitter:title",
					content: post.title,
				},
				{
					name: "twitter:description",
					content: post.summary,
				},
				...(post.image
					? [
							{
								name: "twitter:image",
								content: `https://macrowave.co${post.image}`,
							},
					  ]
					: []),
			],
			scripts: [
				{
					type: "application/ld+json",
					children: JSON.stringify(articleSchema),
				},
				{
					type: "application/ld+json",
					children: JSON.stringify(breadcrumbSchema),
				},
			],
		};
	},
});

function RouteComponent() {
	const { post } = Route.useLoaderData();
	return (
		<article className="max-w-4xl mx-auto py-16 px-6">
			<header className="mb-12">
				<h1 className="text-5xl font-semibold mb-6 tracking-tight leading-tight">
					{post.title}
				</h1>
				<p className="text-xl text-gray-600 leading-relaxed mb-6">
					{post.summary}
				</p>
				{post.author && (
					<div className="flex items-center gap-3 text-sm">
						{post.author.avatar && (
							<img
								src={post.author.avatar}
								alt={post.author.displayName}
								loading="lazy"
								className="w-10 h-10 rounded-full object-cover"
							/>
						)}
						<div className="flex items-center gap-2 text-gray-600">
							<span className="font-medium text-gray-900">
								{post.author.displayName}
							</span>
							<span>·</span>
							<time dateTime={post.publishedAt}>
								{new Date(post.publishedAt).toLocaleDateString('en-US', {
									year: 'numeric',
									month: 'long',
									day: 'numeric'
								})}
							</time>
							<span>·</span>
							<span>{post.readingTime} min read</span>
						</div>
					</div>
				)}
			</header>

			{post.image && (
				<div className="mb-12">
					<img
						src={post.image}
						alt={post.title}
						loading="lazy"
						className="w-full rounded-xl"
					/>
				</div>
			)}

			<div className="prose prose-lg prose-gray max-w-none">
				<MDXContent code={post.mdx} components={{ pre: CodeBlock }} />
			</div>

			{post.author && post.author.bio && (
				<footer className="mt-12 pt-8 border-t border-gray-200">
					<div className="flex gap-4">
						{post.author.avatar && (
							<img
								src={post.author.avatar}
								alt={post.author.displayName}
								loading="lazy"
								className="w-16 h-16 rounded-full object-cover flex-shrink-0"
							/>
						)}
						<div className="flex flex-col gap-3">
							<p className="text-sm font-medium text-gray-900">
								About {post.author.displayName}
							</p>
							<p className="text-gray-600">{post.author.bio}</p>
							<div className="flex gap-4 text-sm">
								{post.author.website && (
									<a
										href={post.author.website}
										target="_blank"
										rel="noopener noreferrer"
										className="text-gray-600 hover:text-gray-900 transition-colors"
									>
										Website →
									</a>
								)}
								{post.author.twitter && (
									<a
										href={post.author.twitter}
										target="_blank"
										rel="noopener noreferrer"
										className="text-gray-600 hover:text-gray-900 transition-colors"
									>
										{post.author.twitter.includes('bsky.app') ? 'Bluesky' : 'Twitter'} →
									</a>
								)}
								{post.author.github && (
									<a
										href={post.author.github}
										target="_blank"
										rel="noopener noreferrer"
										className="text-gray-600 hover:text-gray-900 transition-colors"
									>
										{post.author.github.includes('chaos.social') || post.author.github.includes('mastodon') ? 'Mastodon' : 'GitHub'} →
									</a>
								)}
							</div>
						</div>
					</div>
				</footer>
			)}
		</article>
	);
}
